import type { AuthUser, Permission, PermissionAction } from "@/lib/auth/types";
import { ROUTES } from "@/config/routes";
import { SYSTEM_ROLES } from "@/config/roles";
import { sidebarNavigations } from "@/config/sidebarNavigations";

export const routeMap = new Map(
  sidebarNavigations.flatMap((group) => group.items).map((item) => [item.href, item])
);

export function cn(...classes: (string | undefined | null | false)[]) {
  return classes.filter(Boolean).join(" ");
}

const FEATURE_ALIASES: Record<string, string[]> = {
  "Live Calls": ["Supervisor Dashboard", "Agent Reports", "Telephony Analytics", "Missed Calls"],
};

const hasPermission = (
  permissions: Permission[] = [],
  feature: string,
  action: PermissionAction = "read"
) => {
  if (permissions.some((permission) => permission.feature === feature && permission[action])) {
    return true;
  }
  const aliases = FEATURE_ALIASES[feature];
  if (aliases && aliases.length > 0) {
    return permissions.some((permission) => aliases.includes(permission.feature) && permission[action]);
  }
  return false;
};

export const hasUserPermission = (
  user: Pick<AuthUser, "role" | "permissions" | "isSuperAdmin" | "roleDefinition"> | null | undefined,
  feature: string,
  action: PermissionAction = "read"
) => {
  if (!user) return false;
  if (
    user.isSuperAdmin ||
    user.role === SYSTEM_ROLES.SUPER_ADMIN ||
    user.role === "super admin" ||
    user.roleDefinition?.name === "Super Admin" ||
    user.roleDefinition?.roleId === SYSTEM_ROLES.SUPER_ADMIN
  ) {
    return true;
  }
  return hasPermission(user.permissions ?? [], feature, action);
};

const canAccessRoute = (pathname: string, permissions: Permission[]) => {
  const routePermissionMap: Record<string, string> = {
    [ROUTES.DASHBOARD]: "Dashboard",
    [ROUTES.CRM]: "CRM System",
    [ROUTES.AI_AGENT]: "AI Voice Agent",
    [ROUTES.ROLES_PERMISSIONS]: "Roles & Permissions",
  };

  const requiredFeature = routePermissionMap[pathname];
  if (!requiredFeature) return true;

  return hasPermission(permissions, requiredFeature);
};

export function getIndianGreeting(date = new Date()): string {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Kolkata",
      hour: "numeric",
      hourCycle: "h23",
    });
    const formatted = formatter.format(date);
    let hours = parseInt(formatted, 10);
    if (isNaN(hours)) {
      hours = (date.getUTCHours() + 5 + Math.floor((date.getUTCMinutes() + 30) / 60)) % 24;
    }
    if (hours >= 4 && hours < 12) return "Good Morning";
    if (hours >= 12 && hours < 17) return "Good Afternoon";
    if (hours >= 17 && hours < 21) return "Good Evening";
    return "Good Night";
  } catch {
    const hours = (date.getUTCHours() + 5 + Math.floor((date.getUTCMinutes() + 30) / 60)) % 24;
    if (hours >= 4 && hours < 12) return "Good Morning";
    if (hours >= 12 && hours < 17) return "Good Afternoon";
    if (hours >= 17 && hours < 21) return "Good Evening";
    return "Good Night";
  }
}

/** Strips non-digits and returns the 10-digit national number, preserving numbers starting with 91 */
export function cleanPhoneDigits(phoneStr: string | null | undefined): string {
  const digits = String(phoneStr || "").replace(/\D/g, "");
  if (digits.length === 10) return digits;
  if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
  if (digits.length > 10 && digits.startsWith("91")) return digits.slice(-10);
  return digits;
}

/** Validates that a phone string contains a valid 10-digit number */
export function isValid10DigitPhone(phoneStr: string | null | undefined): boolean {
  return cleanPhoneDigits(phoneStr).length === 10;
}

/** Formats a phone number for dialing in standard E.164 (+91...) format */
export function formatPhoneForDialing(phoneStr: string | null | undefined): string {
  const clean = String(phoneStr || "").trim();
  const digits = clean.replace(/\D/g, "");
  if (digits.length === 10) return "+91" + digits;
  if (digits.length === 12 && digits.startsWith("91")) return "+" + digits;
  if (digits.length > 10 && clean.startsWith("+")) return "+" + digits;
  return digits.length >= 10 ? "+91" + digits.slice(-10) : clean;
}

